import * as Dialog from '@radix-ui/react-dialog';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';

import { useLayer } from './layers.ts';

export interface ModalProps {
	/** Up while true. Mount it while open (and while a way-out animation runs); it reads `open` for the trap. */
	open: boolean;
	/** Asked to close: Escape (or Escape handed down from a toast over it). */
	onClose: () => void;
	/** Where focus starts as it opens (Radix would pick the first button). */
	onOpenFocus?: (() => void) | undefined;
	/** The dialog element itself: its role, name, classes and every pixel are the caller's. */
	children: ReactElement;
}

/**
 * A full-screen dialog with no chrome of its own (the result poster): Radix Dialog with the
 * caller's element as its content, portalled to the body so the page (#root) can go inert
 * behind it. Tab is trapped inside while it's up, Escape closes it, the rest of the page is
 * hidden from a screen reader, and nothing under it hears that Escape. A click elsewhere (a
 * toast, the dev tools) doesn't close it. For anything with a heading row and ✕, use `Sheet`.
 *
 * The page going inert and focus going back are the caller's (`useModalBackground`,
 * `useReturnFocus`), declared where the opener is known, before it mounts.
 */
export function Modal({ open, onClose, onOpenFocus, children }: ModalProps) {
	useLayer(open, onClose);
	const modal = (
		// Still mounted during a way-out animation once closed: Escape doesn't close it twice.
		<Dialog.Root open={open} onOpenChange={(o) => !o && open && onClose()}>
			<Dialog.Content
				asChild
				forceMount
				onOpenAutoFocus={(e) => {
					e.preventDefault();
					onOpenFocus?.();
				}}
				// Focus goes back through useReturnFocus, which knows the fallbacks.
				onCloseAutoFocus={(e) => e.preventDefault()}
				onEscapeKeyDown={(e) => open && e.stopPropagation()}
				onPointerDownOutside={(e) => e.preventDefault()}
				onFocusOutside={(e) => e.preventDefault()}
			>
				{children}
			</Dialog.Content>
		</Dialog.Root>
	);
	return typeof document === 'undefined' ? modal : createPortal(modal, document.body);
}
